import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            // We deliberately don't surface backend errors here — server returns 200 on unknown emails too.
            setSent(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-ph-canvas p-6">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center mb-8">
                    <img src="/players-health-logo.png" alt="Players Health" className="h-9 w-auto" draggable={false} />
                </div>
                <div className="ph-card p-7">
                    {sent ? (
                        <div className="text-center py-2">
                            <div className="w-12 h-12 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                            </div>
                            <h1 className="text-xl font-semibold text-ph-text">Check your email</h1>
                            <p className="text-sm text-ph-muted mt-2">
                                If an account exists for <span className="text-ph-text font-medium">{email}</span>, we've sent a password reset link.
                                The link is good for 1 hour.
                            </p>
                            <Link
                                to="/login"
                                className="inline-block mt-6 text-sm text-ph-primary font-medium hover:text-ph-primaryHover"
                                data-testid="back-to-login-link"
                            >
                                ← Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h1 className="text-xl font-semibold text-ph-text">Forgot your password?</h1>
                            <p className="text-sm text-ph-muted mt-1">
                                Enter your work email and we'll send you a link to reset it.
                            </p>
                            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="forgot-password-form">
                                <div>
                                    <label className="text-xs font-medium text-ph-text mb-1.5 block">Email</label>
                                    <Input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="h-10"
                                        data-testid="forgot-email-input"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-10 bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                                    data-testid="forgot-submit-btn"
                                >
                                    {loading ? "Sending…" : "Send reset link"}
                                </Button>
                            </form>
                            <div className="mt-6 text-sm text-center text-ph-muted">
                                <Link to="/login" className="text-ph-primary font-medium hover:text-ph-primaryHover" data-testid="goto-login-link">
                                    ← Back to sign in
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
